import { useState, useLayoutEffect, useMemo, useCallback } from 'react';

type Breakpoint = { key: string; width: number };

const useContainerBreakpoint = (breakpoints: Breakpoint[]) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [breakpoint, setBreakpoint] = useState<string | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    setElement(node);
  }, []);

  const sorted = useMemo(
    () => [...breakpoints].sort((a, b) => a.width - b.width),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(breakpoints)],
  );

  const check = useCallback(() => {
    if (!element) return;
    const width = element.getBoundingClientRect().width;
    const matched = sorted.find((bp) => width <= bp.width)?.key ?? null;

    // Toggle data attributes directly on the DOM so CSS responds in the same
    // frame as the resize, avoiding the one-frame flash that a React state
    // update + re-render would introduce.
    sorted.forEach((bp) => {
      element.toggleAttribute(`data-${bp.key}`, bp.key === matched);
    });

    setBreakpoint(matched);
  }, [element, sorted]);

  // Synchronous check after every DOM commit to catch layout changes
  // (e.g. sibling panels showing/hiding) before the browser paints.
  useLayoutEffect(() => {
    check();
  });

  // Continuous monitoring via ResizeObserver for external resizes.
  useLayoutEffect(() => {
    if (!element) return;
    const observer = new ResizeObserver(() => check());
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, check]);

  return { ref, breakpoint };
};

export default useContainerBreakpoint;
